import type { RouteMeta } from '@/contexts/FeatureRegistryContext';

/**
 * Central registry of all page-level ROUTE_META.
 * 
 * Pages export their own ROUTE_META constant; this module imports and
 * aggregates them so the Ask Cannafax edge function receives the full
 * list in a single read.
 * 
 * Because pages are lazy-loaded, we maintain a static list here to avoid
 * dynamic-import complexity.  When a new page is created, add its
 * ROUTE_META entry below.
 */

export const ROUTE_REGISTRY: RouteMeta[] = [
  {
    path: '/my-listings',
    title: 'My Listings (Dashboard)',
    description: 'Primary inventory dashboard. Search, filter, bulk-select, and bulk-edit your listings; create new listings; trigger Excel bulk import; learn about email forwarding to ingest@cannafax.ai; or ask suppliers to refresh the inventory you hold for them (Request Inventory Updates).',
    personas: ['seller', 'buyer_seller'],
    concepts: [
      'Listings are individual product entries (strain, form, pricing, COA, region, etc.).',
      'Three ways to add inventory: manual create, bulk Excel import, or email forwarding to ingest@cannafax.ai.',
      'Auto-archive removes stale listings after a configurable window but preserves their connection history.',
      'Request Inventory Updates emails each supplier only their own inventory and asks them to reply with what is still available, sold out, or new — one-off or on a recurring schedule. Replies are tracked, so a "Waiting on N suppliers" chip shows who has not answered.',
      'Closure suggestions from supplier replies are reviewed by a human: listings get a "Sold out?" badge and the seller archives or keeps each one. Ambiguous matches are never archived automatically.',
      'Request Inventory Updates is not Request Missing Info: Missing Info chases empty FIELDS on listings you already have; Inventory Updates chases a SUPPLIER for the current state of their whole inventory.',
    ],
  },
  {
    path: '/my-menu',
    title: 'My Menus',
    description: 'Create and manage menus to organize and share inventory with buyers.',
    personas: ['seller', 'buyer_seller'],
    concepts: [
      'Menus are shareable collections of listings curated for specific buyers or groups',
      "Buyer Access shows each connected company's primary contact; clicking the name opens message, email, call and text actions",
    ],
  },
  {
    path: '/supplier-menus',
    title: 'Supplier Menus',
    description: 'Find shared supplier menus in Suppliers, or open your company menus in Customer view from Our Menus during a sales conversation.',
    personas: ['buyer', 'buyer_seller', 'seller'],
    concepts: ['Suppliers is the default tab; search by supplier or shared menu name.', 'Our Menus searches active company menus and opens each in the existing customer-facing view, not the editor.', 'Each tab keeps its own search while switching; access and source confidentiality stay unchanged.'],
  },
  {
    path: '/buy-requests',
    title: 'Buy Requests',
    description: 'What your company is looking for, and the listings on your supplier menus that fit each request.',
    personas: ['buyer', 'buyer_seller', 'seller'],
    concepts: [
      'A buy request is checked only against supplier menus you already have access to; matches name the supplier whose menu it is on.',
      'Every plan keeps 1 active buy request; Buyer Pro and Power Trader have no limit.',
    ],
  },
  {
    path: '/watchlist',
    title: 'Following',
    description:
      'Everything a buyer is tracking: watched listings, followed suppliers, per-menu alerts, and saved searches, each with its own alert frequency. Also holds notification settings per alert category.',
    personas: ['buyer', 'buyer_seller', 'seller'],
    concepts: [
      'Watching a listing alerts you to price drops and sold-out changes; following a supplier covers everything they share with you; alerting on a single menu is the quieter, menu-only option.',
      'Saved searches are scoped to the menu they were run in — the watchlist shows which supplier and menu each covers, and clicking one reopens that menu with the search re-run.',
      'If a menu is archived, deleted, or your access is revoked, its searches and alerts show "Menu no longer available" instead of erroring, and alerts stop firing.',
      'Each item has its own frequency: instant, daily digest, weekly digest, or off — instant is reserved for price drops and sold-out.',
    ],
  },
  {
    path: '/messages',
    title: 'Messages',
    description: 'Keep one continuous, company-wide conversation with each company in your private network.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: [
      'New activity appears in bold with an unread count, and any conversation can be marked unread for the team',
      'New conversations are limited to established relationships and prior message partners',
      'Listing references remain attached to the individual messages that introduced them',
    ],
  },
  {
    path: '/share-links',
    title: 'Share links',
    description: 'Every listing share link you have sent, with view counts, expiry, copy and revoke.',
    personas: ['seller', 'buyer_seller'],
    concepts: ['Revoke a share link here to stop it working immediately'],
  },
  {
    path: '/desk',
    title: 'Your Desk',
    description: 'Your Desk: Desk Setup until setup is finished, then Your Next Move™ — one priority with its evidence and a conversation with Ask Cannafax that already knows the facts.',
    personas: ['seller', 'buyer_seller'],
    concepts: [
      'Start every workday on Your Desk; the header shows Desk Setup until setup is finished, then Your Next Move™.',
      'Your Next Move™ is one priority at a time, chosen from real activity; Done sets it aside.',
      'On mornings when a move is waiting, the same move is emailed with one button back to Your Desk.',
    ],
  },
  {
    path: '/my-menu/:menuId',
    title: 'Menu Detail',
    description: 'Manage a specific menu — add/remove listings, control visibility, invite buyers.',
    personas: ['seller', 'buyer_seller'],
    concepts: [
      'Each menu has its own subscriber list, visibility controls, and share link',
      'Pending invitations to this menu can be nudged (Resend) or withdrawn (Cancel invitation) from the Buyers tab, within a 24-hour-per-invitee cooldown and a 4-send lifetime limit',
      "Every company with access shows its primary contact, and clicking the name opens message, email, call and text actions",
      'Both lists on the Buyers tab have a fixed scrolling window, typo-tolerant search and a sort control for long lists',
      'Selected listings can be cross-listed into other menus (or a new menu) with Add to Menu in the selection bar, optionally removing them from this menu so it becomes a move',
    ],
  },
  {
    path: '/listings/:id',
    title: 'Listing Detail',
    description: 'Read-only view of a single listing with full COA data, images, documents, and connection history.',
    personas: ['seller', 'buyer_seller'],
    concepts: ['Click Edit to change fields, or use the share button to generate a single-listing share link.'],
  },
  {
    path: '/listings/:id/edit',
    title: 'Edit Listing',
    description: 'Edit a listing\'s details including strain, pricing, COA data, images, and documents.',
    personas: ['seller', 'buyer_seller'],
    concepts: ['Listings can be created manually or imported via bulk import / email forwarding'],
  },
  {
    path: '/listings/new',
    title: 'Create Listing',
    description: 'Manually create a new listing. Uses deferred creation — nothing is saved until you click Save.',
    personas: ['seller', 'buyer_seller'],
    concepts: ['Upload a COA to auto-extract lab data; AI can also draft a description from the strain + terpene profile.'],
  },
  {
    path: '/menu/:companyId/:menuId',
    title: 'Supplier Menu (Buyer View)',
    description: 'Buyer-facing view of a supplier\'s menu. Browse listings, filter, contact the supplier, or copy listings into your own inventory (buyer/sellers).',
    personas: ['buyer', 'buyer_seller', 'seller'],
    concepts: [
      'Seller / source identity is NEVER shown to buyers — only the supplier company is visible.',
      'Use Copy-to-Inventory (buyer/sellers) to clone a listing into your own catalog with the original seller remembered internally.',
    ],
  },
  {
    path: '/menu/:companyId/:menuId/listing/:listingId',
    title: 'Listing Detail (Buyer View)',
    description: 'Buyer-facing single-listing view with redacted supplier identity, "I Want This" inquiry CTAs, and contact-supplier actions.',
    personas: ['buyer', 'buyer_seller', 'seller'],
    concepts: ['Inquiries fire `inquiry_sent` and notify the supplier via the mediated email relay (Reply-To is blinded).'],
  },
  {
    path: '/company/:companyId',
    title: 'Supplier Profile / Menus',
    description: 'All menus a single supplier has shared with you. Pick a menu to browse listings.',
    personas: ['buyer', 'buyer_seller', 'seller'],
    concepts: ['Suppliers may publish multiple menus (e.g. Flower, Concentrates, Reseller-Only).'],
  },
  {
    path: '/menu-invite/:token',
    title: 'Menu Invitation',
    description: 'Landing page when a buyer clicks an emailed menu invitation. Accepts the invite and grants access.',
    personas: ['buyer', 'buyer_seller', 'seller'],
    concepts: ['Invitations are one-time tokens; expired or revoked tokens show a stale-link screen.'],
  },
  {
    path: '/menu-access-request/:requestId',
    title: 'Menu Access Request Status',
    description: 'Shows whether your access request to a private menu has been approved, pending, or declined.',
    personas: ['buyer', 'buyer_seller', 'seller'],
    concepts: ['Suppliers approve/deny access requests from their Menu Detail page.'],
  },
  {
    path: '/share/:id',
    title: 'Shared Listing (Public)',
    description: 'Anonymous-friendly public view of a single shared listing. Sign-up CTAs cache the visitor\'s intent.',
    personas: ['buyer', 'buyer_seller', 'seller'],
    concepts: ['Docs and supplier contact are redacted by default; the supplier controls share visibility per share link.'],
  },
  {
    path: '/s/:slug',
    title: 'Shared Menu Link (Short URL)',
    description: 'Short-URL landing for a shared menu token. Redeems the token and routes to the menu (or to access-request if private).',
    personas: ['buyer', 'buyer_seller', 'seller'],
    concepts: ['Self-link owners are routed back to their own menu for testing.'],
  },
  {
    path: '/onboarding',
    title: 'Onboarding',
    description: 'Mandatory new-user flow: pick persona (seller / buyer/seller / buyer), join or create a company, complete profile.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: [
      'Persona is required and drives which features the user sees.',
      'Sellers and buyer/sellers must have an active subscription to publish menus.',
    ],
  },
  {
    path: '/company-settings',
    title: 'Company Settings',
    description: 'Manage company profile, team members, subscription, and auto-archive settings.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: ['Company settings control your organization profile and team access'],
  },
  {
    path: '/profile-settings',
    title: 'Profile Settings',
    description: 'Update your personal profile, persona, and notification preferences.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: ['Your persona (seller, buyer/seller, or buyer) determines which features you see'],
  },
  {
    path: '/pricing',
    title: 'Plans',
    description:
      'Compare what each plan includes. Self-serve purchase is not live yet — access is enabled account by account through support.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: [
      'The comparison table is generated from the same packaging configuration the app enforces, so it always matches real access.',
      'Nobody is charged today — request access through support and it is enabled on your account.',
    ],
  },
  {
    path: '/',
    title: 'Homepage (public)',
    description:
      'Public marketing homepage. Explains what Cannafax does for sellers, buyer/sellers and buyers, and links to sign in, start free, and book a demo. Signed-in users are redirected to their persona landing page.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: [
      'Cannafax is a private, invite-only network — never a public marketplace, and nothing is publicly listed or indexed.',
      'Core promise: your menu goes out and the interest comes back — sellers see which listings earned buyer attention, buyers see current availability and pricing.',
      'Sign-in and signup live at /auth; the homepage itself contains no auth form.',
    ],
  },
  // ── Sysadmin surfaces (only shown when the AI prompt builder flags isSysAdmin) ──
  {
    path: '/admin',
    title: 'Admin Console (Sysadmin)',
    description: 'System-admin landing page with platform-wide overview.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: ['Sysadmin-only. Visible to users in the `system_admins` table.'],
  },
  {
    path: '/admin/companies',
    title: 'Companies (Admin)',
    description: 'Sysadmin browse of every company on the platform, with drill-down to listings, users, and menus.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: ['Sysadmin-only.'],
  },
  {
    path: '/admin/companies/:companyId',
    title: 'Company Detail (Admin)',
    description: 'Sysadmin deep-dive into a single company\'s listings, users, menus, and admin notes.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: ['Sysadmin-only. Supports cascade-delete via delete_company_cascade.'],
  },
  {
    path: '/admin/platform-analytics',
    title: 'Platform Analytics (Admin)',
    description: 'Two tabs: Habit (Hooked-model loop scoring, leaks, and the habit advisor) and Raw Metrics (engagement, retention, acquisition, cohort, activation funnel).',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: [
      'Sysadmin-only. Excludes test accounts via is_excluded_test_user.',
      'Habit tab scores the Hooked loop (Nir Eyal) per company: Trigger, Action, Variable Reward, Investment.',
      'The AI advisor lives inside the Habit tab; every recommendation is attributed to a Hooked phase and a side.',
    ],
  },
  {
    path: '/admin/system',
    title: 'System & Architecture (Admin)',
    description: 'Sysadmin console with tabs: Monitoring, Invariants (C-* checks + decision log), AI Systems (models grouped by tier with rationale, memory dials, chat starters), Architecture (one sync for inventory/reality/constraints, component inventory, knowledge graph), Platform Access, Maintenance.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: [
      'Sysadmin-only.',
      'All end-user AI features (Ask Cannafax, Listing Description, AI Search, Next Move email, Species Classifier) have their model configured here.',
      'The knowledge graph lives here (Architecture tab); the Habit Playbook node opens via ?slug=intent:strategy-brief. /admin/knowledge redirects here.',
    ],
  },
  {
    path: '/admin/feedback',
    title: 'Feedback (Admin)',
    description: 'Sysadmin triage of user-submitted feedback with closed-loop status history.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: ['Sysadmin-only. Each feedback item has a reference ID shared with the user.'],
  },
  {
    path: '/admin/share-links',
    title: 'Share Links (Admin)',
    description: 'Sysadmin view of every share token across the platform with redemption history.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: ['Sysadmin-only.'],
  },
  {
    path: '/admin/next-move-rules',
    title: 'Your Next Move rules (Admin)',
    description: 'System-admin view: edit, simulate and trial every Your Next Move™ suggestion, with numbers by role and full history.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: ['When a move fires is code (read-only here); order, cooldown, wording, destination and who sees it are editable and versioned.'],
  },
  {
    path: '/admin/jurisdictions',
    title: 'Jurisdictions (Admin)',
    description: 'System-admin view: manage cannabis legal-status data and trigger jurisdiction refreshes.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: ['Jurisdictions drive region disambiguation in AI search and shippability metadata.'],
  },
  {
    path: '/admin/product-types',
    title: 'Product Types & Categories (Admin)',
    description:
      'Review supplier product wording that could not be matched to a standard product type, and confirm how it should be filed.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: [
      'Suppliers keep their own wording; matching and filters run on the standard type it resolves to.',
      'A confirmed mapping is remembered forever and applies to every future listing from every supplier.',
      'Unmatched wording stays live and text-searchable — it is never hidden or deleted.',
    ],
  },
  {
    path: '/admin/activity-catalog',
    title: 'Activity Catalog (Admin)',
    description: 'Sysadmin view of the canonical event taxonomy, instrumentation gaps, and live source-vs-mirror instrumentation drift.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: ['Sysadmin-only. Events flipping from Active to Stale indicate a refactor regression.'],
  },
  {
    path: '/admin/entitlements',
    title: 'Plans & Billing (Admin)',
    description:
      'Define what each plan includes, version packaging changes as draft/published revisions, and grant per-company exceptions.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: [
      'Entitlements are resolved server-side: plan grid + company exceptions collapse into one answer per company.',
      'Packaging changes are versioned revisions — published revisions are immutable, so drafts are the only editable surface.',
      'Watch-only (shadow) mode logs would-be denials instead of blocking, so no customer loses access mid-migration.',
    ],
  },
  {
    path: '/admin/marketing',
    title: 'Marketing Playbook (Admin)',
    description:
      'Live marketing hand-off document: personas, the full capability catalogue with in-app paths, feature spotlights, live plan and pricing data, campaign playbooks, claim safety and voice. Refresh to re-read the product, then download it as markdown.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: [
      'Sysadmin-only. Positioning content is versioned in the repo; plans, limits and the capability list are read live so the document cannot go stale.',
      'Capabilities with no marketing angle yet are listed as gaps rather than silently omitted.',
    ],
  },
  {
    path: '/support',
    title: 'Support',
    description:
      'Public support hub: ask the assistant, book a demo, or send the team a message. Open to visitors and signed-in users.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: [
      'Visitors get product answers only; signed-in users get full how-to help',
      'Signed-in bug and feature reports are filed in the tracked feedback loop',
    ],
  },
  {
    path: '/refund-policy',
    title: 'Refund Policy',
    description:
      'Public, plain-language cancellation and refund terms for cannafax.ai subscriptions. Restates Terms of Service section 6 and links back to the contract as the operative text. Linked in the site footer for card-brand compliance.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: [
      'Cancel anytime from the billing portal in Company Settings; the unused portion of the paid term is refunded pro-rata to the original payment method.',
      'One-time or usage-based charges already rendered are not refundable; statutory consumer rights are unaffected.',
      'The Terms of Service (section 6) govern in case of any conflict with this summary.',
    ],
  },
  {
    path: '/admin/support',
    title: 'Support Requests (Admin)',
    description: 'Sysadmin triage queue for messages submitted through the public support page.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: ['Sysadmin-only.'],
  },
  {
    path: '/settings/email',
    title: 'Email preferences',
    description: 'Choose which optional Cannafax emails you receive.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: [
      'Optional email can be switched off per category; account and security email always sends.',
    ],
  },
  {
    path: '/unsubscribe',
    title: 'Unsubscribe confirmation',
    description:
      'Public page reached from the unsubscribe link in an email. Shows exactly which email category would stop, and nothing is turned off until the recipient confirms.',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: [
      'Unsubscribe links never act on their own — clicking one opens this confirmation page.',
      'The page offers choosing categories at /settings/email instead of switching everything off.',
      'Account and security email always sends and cannot be switched off here.',
    ],
  },
  {
    path: '/brief-frequency',
    title: 'Change brief delivery',
    description:
      'Public page reached from the "send weekly" or "turn it off" links in the Your Next Move™ email. The cadence changes only after confirming here.',
    personas: ['seller', 'buyer_seller'],
    concepts: [
      'Brief cadence is company-wide and applies to everyone who receives the brief email.',
      'Turning the brief email off does not stop the brief — it still appears in Your Desk.',
    ],
  },
  {
    path: '/billing',
    title: 'Billing',
    description:
      'Opens Company Settings → Plan & billing, the unified company plan, saved cards and invoice history.',
    personas: ['buyer', 'seller', 'buyer_seller'],
    concepts: [
      'Your plan determines what capabilities are available — upgrade or change from the pricing page.',
      'Payment methods are tokenized by EPD Elements — card numbers never touch our servers.',
      'Invoice history shows every charge, refund, and payment status.',
    ],
  },
  {
    path: '/referrals',
    title: 'Partner RevShare (moved)',
    description:
      'Redirects to the Partner RevShare tab in Company Settings, where the referral link, referred accounts, commission rate, cleared balance and payout account now live (company admins only).',
    personas: ['seller', 'buyer_seller', 'buyer'],
    concepts: [
      'Partner RevShare lives in Company Settings → Partner RevShare; only company admins can see it because it holds banking details.',
      'You earn a share of what each company you refer actually pays, every month they stay — the rate rises from 15% to 20% to 25% as your active referrals grow.',
      'Bank details are only needed once earnings start accruing, never to join the program.',
    ],
  },
  {
    path: '/r/:code',
    title: 'Referral invitation',
    description:
      'Landing page for a partner referral link. Remembers the referring company for 90 days and starts signup with extended free access.',
    personas: ['buyer', 'seller', 'buyer_seller'],
    concepts: [
      'The last invitation or share link clicked before signup gets the credit; it is locked permanently at signup.',
      'Referral links never expose supplier identity or inventory; they carry only the referring company name.',
    ],
  },
  {
    path: '/j/:code',
    title: 'Share inventory with a buyer',
    description:
      "Landing page for a buyer's invitation. New suppliers join with the normal trial and their first menu is shared with the buyer; suppliers already on Cannafax choose which menus to share.",
    personas: ['seller', 'buyer_seller'],
    concepts: [
      'The buyer who sent the link gets credit if you sign up; buyer invitations carry the normal trial, not the partner offer.',
      'Shared menus appear for the buyer right away — they asked, so there is no accept step.',
      'Nothing is shared until you choose it, except your first menu when you join through the link.',
    ],
  },
];

/**
 * Find the best matching ROUTE_META for a given pathname.
 */
export function findRouteMeta(pathname: string): RouteMeta | undefined {
  // Try exact match first
  const exact = ROUTE_REGISTRY.find((r) => r.path === pathname);
  if (exact) return exact;

  // Try pattern match (e.g. /my-menu/:menuId)
  return ROUTE_REGISTRY.find((r) => {
    if (!r.path.includes(':')) return false;
    const pattern = r.path.replace(/:[^/]+/g, '[^/]+');
    return new RegExp(`^${pattern}$`).test(pathname);
  });
}
